import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ShieldCheck, 
  Trash2, ArrowLeft, Send, 
  ChevronRight, Package,
  Download,
  Zap, Search, X, Loader2, Info, Cpu, FileJson,
  Printer, Sparkles, Edit3,
  Layout, GripVertical, Maximize2
} from 'lucide-react';
import { productService, proposalService, leadService } from '../../../services/erpApi';
import { useAuth } from '../../../contexts/AuthContext';
// Importações estáticas para PDF (mais confiável que dinâmica)
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './NewProposalPage.css';

// Interface para Product
interface Product {
  id: number;
  name: string;
  sku: string;
  price: number;
  image_url?: string;
}

const NewProposalPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proposalRef = useRef<HTMLDivElement>(null);
  
  const [step, setStep] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState<{product: Product, quantity: number}[]>([]);
  const [discount, setDiscount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingProposal, setSavingProposal] = useState(false);
  const [proposalId, setProposalId] = useState<number | null>(null);
  const [proposalLeadId, setProposalLeadId] = useState<number | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProposal, setLoadingProposal] = useState(false);
  const [leadInfo, setLeadInfo] = useState<{ name?: string; institution?: string; cpf_cnpj?: string; email?: string; phone?: string }>({});

  const { user } = useAuth();
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; width: number } | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Estados de Transição e PDF
  const [isPreparing, setIsPreparing] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [engineLogs, setEngineLogs] = useState<string>('Iniciando Motor...');

  useEffect(() => {
  setLoading(true);
    productService.getAll().then(res => {
      if (res.success) setAllProducts(res.data || []);
      setLoading(false);
    }).catch(err => {
      console.error('Erro ao carregar produtos:', err);
      setLoading(false);
    });
  }, []);

  // Carregar proposta para edição (quando houver proposal_id na URL)
  useEffect(() => {
    const proposalIdParam = searchParams.get('proposal_id');
    if (!proposalIdParam) return;

    const loadProposal = async () => {
      try {
        setLoadingProposal(true);
        const res = await proposalService.getById(proposalIdParam);
        if (res.success && res.data) {
          const proposal = res.data;
          setProposalId(proposal.id);
          setProposalLeadId(proposal.lead_id || null);
          setDiscount(proposal.discount_percent || 0);

          const items = (proposal.items || []).map((item: any) => ({
            product: {
              id: item.product_id,
              name: item.product_name || 'Produto',
              sku: item.product_sku || '',
              price: item.unit_price || item.price || 0,
              image_url: item.product_image_url || undefined
            },
            quantity: item.quantity || 1
          }));
          setSelectedProducts(items);
        }
      } catch (error) {
        console.error('Erro ao carregar proposta:', error);
      } finally {
        setLoadingProposal(false);
      }
    };

    loadProposal();
  }, [searchParams, loadingProposal]);

  // Carregar informações do lead para preencher cliente/consultor
  useEffect(() => {
    const leadIdFromParams = searchParams.get('lead_id') ? parseInt(searchParams.get('lead_id')!) : null;
    const effectiveLeadId = leadIdFromParams || proposalLeadId;
    if (!effectiveLeadId) return;

    const loadLead = async () => {
      try {
        const res = await leadService.getById(effectiveLeadId);
        if (res.success && res.data) {
          const data = res.data;
          setLeadInfo({
            name: data.name,
            institution: data.institution,
            cpf_cnpj: data.cpf_cnpj,
            email: data.email,
            phone: data.phone || data.cellphone
          });
        }
      } catch (error) {
        console.error('Erro ao carregar lead:', error);
      }
    };

    loadLead();
  }, [searchParams, proposalLeadId]);

  // Buscar produtos quando houver 2+ caracteres (com debounce)
  useEffect(() => {
  if (loading) return;

    const trimmedTerm = searchTerm.trim();
    
    if (trimmedTerm.length >= 2) {
      setSearchLoading(true);
      
      const timeoutId = setTimeout(() => {
        const searchLower = trimmedTerm.toLowerCase();
        const filtered = allProducts
          .filter((product: Product) => {
            return (
              product.name?.toLowerCase().includes(searchLower) ||
              product.sku?.toLowerCase().includes(searchLower)
            );
          })
          .slice(0, 8); // Limitar a 8 resultados
        
        setSearchResults(filtered);
        setShowSearchDropdown(filtered.length > 0);
        setSearchLoading(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      setSelectedIndex(-1);
    }
  }, [searchTerm, allProducts, loading]);

  // Calcular posição do dropdown quando for exibido
  useEffect(() => {
    if (showSearchDropdown && searchRef.current) {
      const savedPosition = localStorage.getItem('proposal-search-dropdown-position');
      const savedWidth = localStorage.getItem('proposal-search-dropdown-width');
      
      let initialPosition = {
        top: 0,
        left: 0,
        width: 0
      };

      if (savedPosition) {
        try {
          const position = JSON.parse(savedPosition);
          if (position.top && position.left) {
            initialPosition = {
              top: position.top,
              left: position.left,
              width: savedWidth ? parseFloat(savedWidth) : searchRef.current.getBoundingClientRect().width
            };
          }
        } catch (e) {
          console.error('Erro ao carregar posição salva:', e);
        }
      }

      // Se não há posição salva, usar posição padrão
      if (!initialPosition.top && !initialPosition.left) {
        const rect = searchRef.current.getBoundingClientRect();
        initialPosition = {
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: savedWidth ? parseFloat(savedWidth) : rect.width
        };
      }

      setDropdownPosition(initialPosition);
    } else {
      setDropdownPosition(null);
    }
  }, [showSearchDropdown, searchResults]);

  // Atualizar posição ao rolar a página (apenas se não estiver arrastando/redimensionando)
  useEffect(() => {
    if (!showSearchDropdown || isDragging || isResizing) return;

    const updatePosition = () => {
      if (searchRef.current && !isDragging && !isResizing) {
        const savedPosition = localStorage.getItem('proposal-search-dropdown-position');
        // Se há posição salva, não atualizar automaticamente
        if (savedPosition) return;
        
        const rect = searchRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSearchDropdown, isDragging, isResizing]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const handleSearchProductClick = (product: Product) => {
    handleAddProduct(product);
    setSearchTerm('');
    setShowSearchDropdown(false);
    setSelectedIndex(-1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (searchLoading) return;
    
    if (!showSearchDropdown || searchResults.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleSearchProductClick(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSearchDropdown(false);
        setSelectedIndex(-1);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        break;
      default:
        break;
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchDropdown(false);
    setSelectedIndex(-1);
  };

  // Handlers de Drag
  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropdownPosition) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - dropdownPosition.left,
      y: e.clientY - dropdownPosition.top
    });
  };

  // Handlers de Resize
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropdownPosition) return;
    
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      width: dropdownPosition.width
    });
  };

  // Event listeners globais para drag e resize
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dragStart && dropdownPosition) {
        const newLeft = e.clientX - dragStart.x;
        const newTop = e.clientY - dragStart.y;
        
        // Limitar dentro da viewport
        const maxLeft = window.innerWidth - dropdownPosition.width;
        const maxTop = window.innerHeight - 200; // altura mínima estimada
        
        const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
        const constrainedTop = Math.max(0, Math.min(newTop, maxTop));
        
        setDropdownPosition({
          ...dropdownPosition,
          left: constrainedLeft,
          top: constrainedTop
        });
      } else if (isResizing && resizeStart && dropdownPosition) {
        const deltaX = e.clientX - resizeStart.x;
        const newWidth = Math.max(300, Math.min(800, resizeStart.width + deltaX));
        
        setDropdownPosition({
          ...dropdownPosition,
          width: newWidth
        });
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        if (dropdownPosition) {
          // Salvar posição
          localStorage.setItem('proposal-search-dropdown-position', JSON.stringify({
            top: dropdownPosition.top,
            left: dropdownPosition.left
          }));
        }
        setDragStart(null);
      }
      
      if (isResizing) {
        setIsResizing(false);
        if (dropdownPosition) {
          // Salvar largura
          localStorage.setItem('proposal-search-dropdown-width', dropdownPosition.width.toString());
        }
        setResizeStart(null);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, dropdownPosition]);

  const subtotal = selectedProducts.reduce((acc, curr) => acc + (curr.product.price * curr.quantity), 0);
  const total = subtotal * (1 - discount / 100);

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const handleAddProduct = (product: Product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.product.id === product.id);
      if (exists) return prev.map(p => p.product.id === product.id ? { ...p, quantity: p.quantity + 1 } : p);
      return [...prev, { product, quantity: 1 }];
    });
  };

  const goToPreview = async () => {
    if (selectedProducts.length === 0) {
      return;
    }

    try {
      setSavingProposal(true);
      setIsPreparing(true);

      // Pegar lead_id da URL se disponível
      const leadIdFromParams = searchParams.get('lead_id') ? parseInt(searchParams.get('lead_id')!) : null;
      const leadId = leadIdFromParams || proposalLeadId;

      if (!leadId) {
        throw new Error('Lead não informado. Abra a proposta a partir de um lead.');
      }

      // Calcular valores
      const subtotal = selectedProducts.reduce((acc, curr) => acc + (curr.product.price * curr.quantity), 0);
      const discountAmount = subtotal * (discount / 100);

      // Preparar itens da proposta
      const items = selectedProducts.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.product.price,
        discount_percent: 0,
        discount_amount: 0,
        observation: null
      }));

      // Criar proposta
      const proposalData: any = {
        lead_id: leadId,
        validity_days: 15,
        discount_percent: discount,
        discount_amount: discountAmount,
        observation: null
      };

      let response;
      if (proposalId) {
        response = await proposalService.update(proposalId, {
          ...proposalData,
          items: items
        });
      } else {
        response = await proposalService.create({
          ...proposalData,
          items: items
        });
      }

      if (response.success) {
        setProposalId(response.data.id);
        setProposalLeadId(response.data.lead_id || leadId);
        await new Promise(resolve => setTimeout(resolve, 800));
        setStep(2);
      } else {
        throw new Error(response.message || 'Erro ao salvar proposta');
      }
    } catch (error: any) {
      console.error('Erro ao salvar proposta:', error);
      alert('Erro ao salvar proposta: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setSavingProposal(false);
      setIsPreparing(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!proposalRef.current) return;
    
    setGeneratingPDF(true);
    setEngineLogs('Preparando documento...');
    
    try {
      setEngineLogs('Renderizando documento...');
      const canvas = await html2canvas(proposalRef.current, {
        scale: 2.5 as any,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        logging: false,
        windowWidth: 1200 as any,
      } as any);
      
      setEngineLogs('Gerando PDF...');
      await new Promise(r => setTimeout(r, 400));
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      let heightLeft = pdfHeight;
      let position = 0;

      setEngineLogs('Finalizando...');
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }
      
      setEngineLogs('Concluído!');
      await new Promise(r => setTimeout(r, 400));
      pdf.save(`Proposta_${new Date().getFullYear()}_${Date.now()}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Houve um erro técnico ao processar o documento. Certifique-se de que as bibliotecas html2canvas e jspdf estão instaladas.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <div className="new-proposal-page">

      {/* OVERLAY DE TRANSIÇÃO (Step 1 -> 2) */}
      {isPreparing && (
        <div className="proposal-overlay proposal-overlay-preparing">
           <div className="proposal-overlay-icon">
              <FileJson size={40} style={{ color: 'white' }} />
           </div>
           <h3 className="proposal-overlay-title">Compilando Proposta</h3>
           <p className="proposal-overlay-subtitle">Sincronizando tabelas de preços e estoque...</p>
           <div className="proposal-overlay-progress-bar">
              <div className="proposal-overlay-progress-fill animate-progress" />
           </div>
        </div>
      )}

      {/* OVERLAY DE GERAÇÃO DE PDF (Ação Step 2) */}
      {generatingPDF && (
        <div className="proposal-overlay proposal-overlay-pdf">
           <div className="proposal-pdf-overlay-content">
              <div className="proposal-pdf-icon-wrapper">
                 <Cpu size={32} className="animate-spin" />
                 <div className="animate-scan" />
              </div>
              <div>
                 <h4 className="proposal-pdf-title">Gerando PDF</h4>
                 <p className="proposal-pdf-subtitle">Exportando Documento</p>
              </div>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                 <div className="proposal-pdf-log">
                    <p className="proposal-pdf-log-text">
                      {engineLogs}
                    </p>
                 </div>
                 <div className="proposal-pdf-progress-bar">
                    <div className="proposal-pdf-progress-fill animate-progress-fast" />
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* Header Navigation */}
      <div className="no-print proposal-header-nav">
        <button onClick={() => navigate('/erp/prospecting')} className="proposal-back-button">
          <ArrowLeft size={16} /> Voltar para Hub
        </button>
        <div className="proposal-step-indicator">
           <div className={`proposal-step-dot ${step >= 1 ? 'active' : ''}`} />
           <div className="proposal-step-line"><div className={`proposal-step-line-fill ${step >= 2 ? 'active' : ''}`} /></div>
           <div className={`proposal-step-dot ${step >= 2 ? 'active' : ''}`} />
        </div>
      </div>

      <div className="proposal-content-grid">
        
        {/* Left Column */}
        <div className="proposal-main-column">
           
           {step === 1 ? (
             <div className="no-print">
                <div className="proposal-composition-card">
                   <div className="proposal-composition-decoration" />
                   
                   <div className="proposal-composition-header">
                      <div className="proposal-composition-icon">
                        <Package size={28} />
                      </div>
                      <div>
                        <h3 className="proposal-composition-title">Composição</h3>
                        <p className="proposal-composition-subtitle">Escolha os produtos e quantidades</p>
                      </div>
                   </div>

                   {/* Search with Dropdown */}
                   <div className="proposal-search-container" ref={searchRef}>
                      <form 
                        className="proposal-search-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
                            handleSearchProductClick(searchResults[selectedIndex]);
                          }
                        }}
                      >
                        <div className="proposal-search-icon-wrapper">
                          <Search size={18} className="proposal-search-icon" />
                        </div>
                        <input 
                          type="text" 
                          placeholder="Busque por produtos, SKU..."
                          className="proposal-search-input"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSelectedIndex(-1);
                            if (e.target.value.trim().length === 0) {
                              setShowSearchDropdown(false);
                              setSearchResults([]);
                            }
                          }}
                          onKeyDown={handleSearchKeyDown}
                          onFocus={() => {
                            const trimmed = searchTerm.trim();
                            if (trimmed.length >= 2 && searchResults.length > 0) {
                              setShowSearchDropdown(true);
                            }
                          }}
                          ref={inputRef}
                          autoComplete="off"
                        />
                        <div className="proposal-search-actions">
                          {searchTerm && (
                            <button 
                              type="button" 
                              onClick={clearSearch} 
                              className="proposal-search-clear"
                              aria-label="Limpar busca"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      </form>

                      {typeof document !== 'undefined' && (showSearchDropdown || searchLoading) && searchTerm.trim().length >= 2 && dropdownPosition && createPortal(
                        <div 
                          className="proposal-search-dropdown proposal-search-dropdown-fixed" 
                          ref={dropdownRef}
                          style={{
                            position: 'fixed',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                            zIndex: 99999
                          }}
                        >
                          {searchLoading ? (
                            <div className="proposal-search-loading">
                              <Loader2 size={16} className="animate-spin" />
                              <span>Buscando produtos...</span>
                            </div>
                          ) : searchResults.length > 0 ? (
                            <>
                              <div 
                                className="proposal-search-dropdown-header proposal-search-dropdown-header-draggable"
                                onMouseDown={handleDragStart}
                                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                              >
                                <GripVertical size={14} className="proposal-search-drag-handle" />
                                <span>Sugestões de produtos</span>
                              </div>
                              <div className="proposal-search-dropdown-results">
                                {searchResults.map((product, index) => {
                                  const isSelected = index === selectedIndex;
                                  
                                  return (
                                    <button
                                      key={product.id}
                                      type="button"
                                      className={`proposal-search-dropdown-item ${isSelected ? 'selected' : ''}`}
                                      onClick={() => handleSearchProductClick(product)}
                                    >
                                      <div className="proposal-search-item-image">
                                        {product.image_url ? (
                                          <img 
                                            src={product.image_url} 
                                            alt={product.name}
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <Package size={24} />
                                        )}
                                      </div>
                                      <div className="proposal-search-item-content">
                                        <div className="proposal-search-item-name">{product.name}</div>
                                        {product.sku && (
                                          <div className="proposal-search-item-sku">SKU: {product.sku}</div>
                                        )}
                                      </div>
                                      <div className="proposal-search-item-price">
                                        {formatCurrency(product.price)}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          ) : (
                            <div className="proposal-search-dropdown-empty">
                              <Package size={32} />
                              <span>Nenhum produto encontrado</span>
                            </div>
                          )}
                          {/* Resize Handle */}
                          <div 
                            className="proposal-search-dropdown-resize-handle"
                            onMouseDown={handleResizeStart}
                            style={{ cursor: isResizing ? 'col-resize' : 'ew-resize' }}
                          >
                            <Maximize2 size={12} />
                          </div>
                        </div>,
                        document.body
                      )}
                   </div>

                   <div className="proposal-selected-products">
                      <p className="proposal-selected-header">
                        <Layout size={14} /> Itens na Proposta ({selectedProducts.length})
                      </p>
                      {selectedProducts.map((item, idx) => (
                        <div key={idx} className="proposal-selected-item">
                           <div className="proposal-selected-item-content">
                              <div className="proposal-selected-item-image">
                                {item.product.image_url ? (
                                  <img src={item.product.image_url} alt={item.product.name} />
                                ) : (
                                  <Package size={20} />
                                )}
                              </div>
                              <div className="proposal-selected-item-info">
                                <span className="proposal-selected-item-name">{item.product.name}</span>
                                <span className="proposal-selected-item-price">{formatCurrency(item.product.price)}</span>
                              </div>
                           </div>
                           <div className="proposal-selected-item-actions">
                              <input 
                                type="number" 
                                value={item.quantity} 
                                onChange={(e) => {
                                  const val = parseInt(e.target.value);
                                  setSelectedProducts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: isNaN(val) ? 1 : Math.max(1, val) } : p));
                                }}
                                className="proposal-quantity-input"
                              />
                              <button onClick={() => setSelectedProducts(prev => prev.filter((_, i) => i !== idx))} className="proposal-remove-button">
                                <Trash2 size={18} />
                              </button>
                           </div>
                        </div>
                      ))}
                   </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                   <button 
                     onClick={goToPreview} 
                     disabled={selectedProducts.length === 0 || savingProposal} 
                     className="proposal-preview-button"
                   >
                     {savingProposal ? (
                       <>Salvando Proposta... <Loader2 size={18} className="animate-spin" /></>
                     ) : (
                       <>Gerar Prévia da Proposta <ChevronRight size={18} /></>
                     )}
                   </button>
                </div>
             </div>
           ) : (
             <div>
                
                {/* Actions Toolbar */}
                <div className="no-print proposal-actions-toolbar">
                   <button onClick={() => setStep(1)} className="proposal-edit-button">
                     <Edit3 size={14} /> Ajustar Composição
                   </button>
                   <div className="proposal-actions-group">
                      <button onClick={() => window.print()} className="proposal-action-button">
                        <Printer size={16} /> 
                        <span>Imprimir</span>
                      </button>
                      <button onClick={handleDownloadPDF} className="proposal-action-button">
                        <Download size={16} /> 
                        <span>Baixar PDF</span>
                      </button>
                      <button className="proposal-action-button primary">
                        <Send size={18} /> Transmitir p/ Cliente
                      </button>
                   </div>
                </div>

                {/* Proposal Canvas */}
                <div ref={proposalRef} className="print-area proposal-canvas">
                   
                   {/* Digital ID Strip */}
                   <div className="proposal-canvas-strip no-print" />
                   
                   <div className="proposal-canvas-header">
                      <div className="proposal-canvas-header-decoration" />
                      <div className="proposal-canvas-header-content">
                         <div className="proposal-canvas-logo">
                            <div className="proposal-canvas-logo-icon">PM</div>
                            <span className="proposal-canvas-logo-text">med</span>
                         </div>
                         <h1 className="proposal-canvas-title">Proposta Comercial</h1>
                         <p className="proposal-canvas-doc-number">Doc: #PR-{new Date().getFullYear()}-{String(Date.now()).slice(-4)}</p>
                      </div>
                      <div className="proposal-canvas-header-info">
                         <div className="proposal-canvas-info-group">
                            <p className="proposal-canvas-info-label">Data de Emissão</p>
                            <p className="proposal-canvas-info-value">{new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                         </div>
                         <div className="proposal-canvas-info-group">
                            <p className="proposal-canvas-info-label">Validade Proposta</p>
                            <p className="proposal-canvas-info-value primary">15 Dias Corridos</p>
                         </div>
                      </div>
                   </div>
                   
                   <div className="proposal-canvas-body">
                      <div className="proposal-canvas-parties">
                         <div className="proposal-canvas-party">
                            <h4 className="proposal-canvas-party-title">Dados do Destinatário</h4>
                            <div>
                               <p className="proposal-canvas-party-name">{leadInfo.name || 'Cliente'}</p>
                               <p className="proposal-canvas-party-detail">{leadInfo.institution || 'Empresa / Instituição'}</p>
                               <p className="proposal-canvas-party-cnpj">
                                 {leadInfo.cpf_cnpj ? `CPF/CNPJ: ${leadInfo.cpf_cnpj}` : 'CPF/CNPJ não informado'}
                               </p>
                               <p className="proposal-canvas-party-cnpj">
                                 {leadInfo.email || leadInfo.phone || 'Contato não informado'}
                               </p>
                            </div>
                         </div>
                         <div className="proposal-canvas-party" style={{ textAlign: 'right' }}>
                            <h4 className="proposal-canvas-party-title">Consultor Responsável</h4>
                            <div>
                               <p className="proposal-canvas-party-name">{user?.name || 'Consultor'}</p>
                               <p className="proposal-canvas-party-detail primary">{user?.role || 'Consultor Comercial'}</p>
                               <p className="proposal-canvas-party-cnpj">{user?.email || 'email não informado'}</p>
                            </div>
                         </div>
                      </div>

                      <div>
                         <table className="proposal-canvas-table">
                            <thead>
                               <tr>
                                  <th>Descrição Técnica</th>
                                  <th className="center">Qtd</th>
                                  <th className="right">Unitário</th>
                                  <th className="right">Total</th>
                               </tr>
                            </thead>
                            <tbody>
                               {selectedProducts.map((item, i) => (
                                 <tr key={i}>
                                    <td>
                                       <p className="proposal-canvas-product-name">{item.product.name}</p>
                                       <p className="proposal-canvas-product-sku">SKU: {item.product.sku || 'N/A'}</p>
                                       <p className="proposal-canvas-product-note">Garantia estendida de 12 meses direto com o fabricante.</p>
                                    </td>
                                    <td className="center">{item.quantity}</td>
                                    <td className="right">{formatCurrency(item.product.price)}</td>
                                    <td className="right">{formatCurrency(item.product.price * item.quantity)}</td>
                                 </tr>
                               ))}
                            </tbody>
                         </table>
                      </div>

                      {/* Payment Terms Section in PDF */}
                      <div className="proposal-canvas-summary">
                         <div className="proposal-canvas-notes">
                            <h4 className="proposal-canvas-notes-title">
                               <ShieldCheck size={14} /> Notas Importantes
                            </h4>
                            <ul className="proposal-canvas-notes-list">
                               <li>• Faturamento via Boleto Bancário 30/60 Dias (Sujeito a Crédito).</li>
                               <li>• Frete incluso via Logística Express (Capitais).</li>
                               <li>• Produtos com certificação ANVISA.</li>
                            </ul>
                         </div>
                         <div className="proposal-canvas-totals">
                            <div>
                               <div className="proposal-canvas-total-row">
                                  <span>Subtotal Itens</span>
                                  <span className="proposal-canvas-total-row-value">{formatCurrency(subtotal)}</span>
                               </div>
                               <div className="proposal-canvas-total-row primary">
                                  <span>Desconto Institucional ({discount}%)</span>
                                  <span className="proposal-canvas-total-row-value">- {formatCurrency(subtotal * (discount / 100))}</span>
                               </div>
                               <div className="proposal-canvas-total-row">
                                  <span>Impostos (IPI/ICMS)</span>
                                  <span className="proposal-canvas-total-row-value">Inclusos</span>
                               </div>
                            </div>
                            <div className="proposal-canvas-final-total">
                               <p className="proposal-canvas-final-total-label">Total Final da Proposta</p>
                               <p className="proposal-canvas-final-total-value">{formatCurrency(total)}</p>
                            </div>
                         </div>
                      </div>
                   </div>

                   {/* Legal Footer Strip */}
                   <div className="proposal-canvas-footer">
                      <p className="proposal-canvas-footer-text">Pró Médico • Praça 7 Julho nº 2, Centro - Pelotas</p>
                   </div>
                </div>
             </div>
           )}

        </div>

        {/* Right Column (Tips) - Hidden on Print */}
        <div className="no-print proposal-sidebar">
           <div className="proposal-sidebar-card">
              <div className="proposal-sidebar-card-decoration" />
              
              <div className="proposal-sidebar-content">
                <h3 className="proposal-sidebar-title">
                  <Sparkles size={16} style={{ color: '#d97706' }} /> Auditoria de Venda
                </h3>
                
                <div>
                   <div className="proposal-sidebar-stat">
                      <div className="proposal-sidebar-stat-icon">
                         <Zap size={24} />
                      </div>
                      <div>
                         <p className="proposal-sidebar-stat-label">Chance de Fechamento</p>
                         <p className="proposal-sidebar-stat-value">88.4%</p>
                      </div>
                   </div>

                   <div className="proposal-sidebar-insight">
                      <p className="proposal-sidebar-insight-title">
                        <Info size={14} style={{ color: '#22c55e' }} /> Insight Inteligente
                      </p>
                      <p className="proposal-sidebar-insight-text">
                         Destaque a garantia de 12 meses ao enviar este PDF.
                      </p>
                   </div>
                </div>

                <div className="proposal-sidebar-actions">
                   <button className="proposal-sidebar-button">
                      Histórico do Cliente <ChevronRight size={16} className="proposal-sidebar-button-icon" />
                   </button>
                </div>
              </div>
           </div>
        </div>

      </div>
    </div>
  );
};

export default NewProposalPage;
